// simulator/local.js
import { fmtDT, hashSnapshot } from './utils.js';
import { listItemTemplate } from './templates.js';

export function initLocalViewer({
    UPDATED_FIELD,
    POLL_MS = 1500,
    idbReader,
    remoteApi,
    elements,
}) {
    const {
        listEl,
        lastRefreshEl,
        lastCheckedEl,
        dbStatusEl,
        watchStatusEl,
        noticeEl,
        spinnerEl,
    } = elements;

    let pollTimer = null;
    let lastHash = '';

    function render(items) {
        if (!listEl) return;
        if (!items?.length) {
            listEl.innerHTML = `<li class="list-group-item"><em class="text-body-secondary">No badges found.</em></li>`;
            return;
        }
        listEl.innerHTML = items.map((it) => listItemTemplate(it, UPDATED_FIELD)).join('');
    }

    async function refresh({ forceRender = false } = {}) {
        spinnerEl?.classList?.remove('d-none');
        lastCheckedEl && (lastCheckedEl.textContent = fmtDT(new Date()));
        try {
            // Local (IndexedDB)
            const items = await idbReader.fetchLatest10();
            const hash = hashSnapshot(items, UPDATED_FIELD);
            if (forceRender || hash !== lastHash) {
                lastHash = hash;
                render(items);
                lastRefreshEl && (lastRefreshEl.textContent = fmtDT(new Date()));
            }
            dbStatusEl && (dbStatusEl.textContent = 'connected');
            dbStatusEl && (dbStatusEl.className = 'badge rounded-pill text-bg-success');

            // Remote (throttled)
            if (Date.now() - remoteApi.lastRemoteFetch >= remoteApi.REMOTE_POLL_MS) {
                remoteApi.fetchRemoteBadges(elements.lwDispatch);
            }
        } catch (err) {
            dbStatusEl && (dbStatusEl.textContent = 'error');
            dbStatusEl && (dbStatusEl.className = 'badge rounded-pill text-bg-danger');
            noticeEl && (noticeEl.innerHTML = `<span class="text-danger fw-semibold">Read error:</span> ${err?.message || err}`);
        } finally {
            spinnerEl?.classList?.add('d-none');
        }
    }

    function startPolling() {
        if (pollTimer) clearInterval(pollTimer);
        pollTimer = setInterval(refresh, POLL_MS);
        watchStatusEl && (watchStatusEl.textContent = `watching (poll ${POLL_MS}ms)`);
        watchStatusEl && (watchStatusEl.className = 'badge rounded-pill text-bg-secondary');
    }

    function setupInstantHooks(EVENT_ID) {
        try {
            const bc = new BroadcastChannel(`checkin-${EVENT_ID}-badges-updates`);
            bc.onmessage = () => {
                refresh({ forceRender: true });
                // Also opportunistically refresh remote (throttled)
                if (Date.now() - remoteApi.lastRemoteFetch >= remoteApi.REMOTE_POLL_MS) {
                    remoteApi.fetchRemoteBadges(elements.lwDispatch);
                }
            };
            watchStatusEl && (watchStatusEl.textContent = 'watching (instant + poll)');
            watchStatusEl && (watchStatusEl.className = 'badge rounded-pill text-bg-success');
        } catch {
            // BroadcastChannel not supported — polling only
        }
        window.addEventListener('storage', (ev) => {
            if (ev.key === `checkin-${EVENT_ID}-badges-ping`) {
                refresh({ forceRender: true });
                if (Date.now() - remoteApi.lastRemoteFetch >= remoteApi.REMOTE_POLL_MS) {
                    remoteApi.fetchRemoteBadges(elements.lwDispatch);
                }
            }
        });
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                refresh({ forceRender: true });
                if (Date.now() - remoteApi.lastRemoteFetch >= remoteApi.REMOTE_POLL_MS) {
                    remoteApi.fetchRemoteBadges(elements.lwDispatch);
                }
            }
        });
        window.addEventListener('online', () => {
            refresh({ forceRender: true });
            remoteApi.fetchRemoteBadges(elements.lwDispatch);
        });
    }

    // --- Pause integration (inside initLocalViewer) ---
    let __simPaused = !!(window.badgeTicker?.paused);
    window.addEventListener('badgeTicker:pause', () => { __simPaused = true; });
    window.addEventListener('badgeTicker:resume', () => { __simPaused = false; });

    const __origRefresh = typeof refresh === 'function' ? refresh : null;
    if (__origRefresh) {
        refresh = function (...args) {
            if (__simPaused) {
                try {
                    lastCheckedEl && (lastCheckedEl.textContent = fmtDT(new Date()));
                    if (dbStatusEl) {
                        dbStatusEl.textContent = 'paused';
                        dbStatusEl.className = 'badge rounded-pill text-bg-secondary';
                    }
                } catch { }
                return;
            }
            return __origRefresh.apply(this, args);
        };
    }

    return {
        refresh,
        startPolling,
        setupInstantHooks,
    };
}
